import { useEffect, useRef, useCallback } from 'react';

interface Star {
  x: number;
  y: number;
  z: number;
  baseX: number;
  baseY: number;
  size: number;
  opacity: number;
  twinkleSpeed: number;
  twinkleOffset: number;
  color: string;
  velocity: { x: number; y: number };
  orbitRadius: number;
  orbitSpeed: number;
  orbitOffset: number;
}

interface ShootingStar {
  x: number;
  y: number;
  length: number;
  speed: number;
  angle: number;
  opacity: number;
  life: number;
}

interface Ripple {
  x: number;
  y: number;
  radius: number;
  maxRadius: number;
  opacity: number;
  color: string;
  speed: number;
}

interface Particle {
  x: number;
  y: number;
  vx: number;
  vy: number;
  size: number;
  opacity: number;
  life: number;
  color: string;
  gravity: number;
}

interface DustParticle {
  x: number;
  y: number;
  size: number;
  opacity: number;
  speedX: number;
  speedY: number;
  wobbleSpeed: number;
  wobbleOffset: number;
}

interface EnergyPulse {
  x: number;
  y: number;
  radius: number;
  maxRadius: number;
  opacity: number;
  width: number;
}

const InteractiveStarfield = () => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const starsRef = useRef<Star[]>([]);
  const shootingStarsRef = useRef<ShootingStar[]>([]);
  const ripplesRef = useRef<Ripple[]>([]);
  const particlesRef = useRef<Particle[]>([]);
  const dustRef = useRef<DustParticle[]>([]);
  const energyPulsesRef = useRef<EnergyPulse[]>([]);
  const mouseRef = useRef({ x: 0, y: 0, targetX: 0, targetY: 0, isMoving: false, speed: 0 });
  const animationRef = useRef<number>();
  const timeRef = useRef(0);
  const lastMousePos = useRef({ x: 0, y: 0 });
  const auroraPhaseRef = useRef(0);
  const lastPulseTimeRef = useRef(0);
  const idleTimeRef = useRef(0);

  const colors = [
    'rgba(167, 139, 250, 1)',   // Purple
    'rgba(129, 140, 248, 1)',   // Light purple
    'rgba(167, 139, 250, 1)',   // Pink/magenta
    'rgba(167, 139, 250, 1)',   // Pink
    'rgba(255, 255, 255, 1)',   // White
    'rgba(199, 210, 254, 1)',   // Light blue-purple
    'rgba(139, 92, 246, 1)',    // Violet
  ];

  const createStars = useCallback((width: number, height: number) => {
    const stars: Star[] = [];
    const starCount = Math.floor((width * height) / 2500);

    for (let i = 0; i < starCount; i++) {
      const x = Math.random() * width;
      const y = Math.random() * height;
      stars.push({
        x,
        y,
        baseX: x,
        baseY: y,
        z: Math.random() * 4 + 1,
        size: Math.random() * 2.5 + 0.5,
        opacity: Math.random() * 0.5 + 0.4,
        twinkleSpeed: Math.random() * 3 + 1,
        twinkleOffset: Math.random() * Math.PI * 2,
        color: colors[Math.floor(Math.random() * colors.length)],
        velocity: { x: (Math.random() - 0.5) * 0.3, y: (Math.random() - 0.5) * 0.3 },
        orbitRadius: Math.random() * 30 + 10,
        orbitSpeed: (Math.random() * 0.5 + 0.2) * (Math.random() > 0.5 ? 1 : -1),
        orbitOffset: Math.random() * Math.PI * 2,
      });
    }
    return stars;
  }, [colors]);

  const createDustParticles = useCallback((width: number, height: number) => {
    const dust: DustParticle[] = [];
    const dustCount = Math.floor((width * height) / 8000);

    for (let i = 0; i < dustCount; i++) {
      dust.push({
        x: Math.random() * width,
        y: Math.random() * height,
        size: Math.random() * 1.5 + 0.3,
        opacity: Math.random() * 0.3 + 0.1,
        speedX: (Math.random() - 0.5) * 0.3,
        speedY: (Math.random() - 0.5) * 0.2 - 0.1, // Slight upward drift
        wobbleSpeed: Math.random() * 2 + 0.5,
        wobbleOffset: Math.random() * Math.PI * 2,
      });
    }
    return dust;
  }, []);

  const createShootingStar = useCallback((width: number, height: number): ShootingStar => {
    const startFromTop = Math.random() > 0.5;
    return {
      x: startFromTop ? Math.random() * width : 0,
      y: startFromTop ? 0 : Math.random() * height * 0.5,
      length: Math.random() * 100 + 50,
      speed: Math.random() * 15 + 10,
      angle: Math.PI / 4 + (Math.random() - 0.5) * 0.3,
      opacity: 1,
      life: 1,
    };
  }, []);

  const createRipple = useCallback((x: number, y: number): Ripple => {
    return {
      x,
      y,
      radius: 0,
      maxRadius: 300 + Math.random() * 100,
      opacity: 0.6,
      color: colors[Math.floor(Math.random() * colors.length)],
      speed: 4 + Math.random() * 2,
    };
  }, [colors]);

  const createParticleBurst = useCallback((x: number, y: number, count: number = 20) => {
    const newParticles: Particle[] = [];
    for (let i = 0; i < count; i++) {
      const angle = (Math.PI * 2 * i) / count + (Math.random() - 0.5) * 0.5;
      const speed = 3 + Math.random() * 5;
      newParticles.push({
        x,
        y,
        vx: Math.cos(angle) * speed,
        vy: Math.sin(angle) * speed,
        size: Math.random() * 3 + 1,
        opacity: 1,
        life: 1,
        color: colors[Math.floor(Math.random() * colors.length)],
        gravity: 0.05 + Math.random() * 0.05,
      });
    }
    return newParticles;
  }, [colors]);

  const createEnergyPulse = useCallback((x: number, y: number): EnergyPulse => {
    return {
      x,
      y,
      radius: 0,
      maxRadius: 500,
      opacity: 0.4,
      width: 3,
    };
  }, []);

  const drawStar = useCallback((
    ctx: CanvasRenderingContext2D,
    star: Star,
    time: number,
    mouseOffsetX: number,
    mouseOffsetY: number,
    mouseSpeed: number,
    width: number,
    height: number
  ) => {
    // Autonomous orbital movement
    const orbitX = Math.cos(time * star.orbitSpeed + star.orbitOffset) * star.orbitRadius * (star.z / 4);
    const orbitY = Math.sin(time * star.orbitSpeed + star.orbitOffset) * star.orbitRadius * (star.z / 4);

    // Parallax based on mouse
    const parallaxX = mouseOffsetX * (star.z * 0.8);
    const parallaxY = mouseOffsetY * (star.z * 0.8);

    // Breathing effect - stars move in and out slightly
    const breathe = Math.sin(time * 0.5 + star.twinkleOffset) * 5 * (star.z / 4);

    // Calculate base position
    let x = star.baseX + orbitX + parallaxX + breathe;
    let y = star.baseY + orbitY + parallaxY + breathe * 0.5;

    // Gravitational attraction to cursor
    const dx = mouseRef.current.x - x;
    const dy = mouseRef.current.y - y;
    const distanceFromMouse = Math.sqrt(dx * dx + dy * dy);
    const attractionRadius = 250;

    if (distanceFromMouse < attractionRadius && distanceFromMouse > 10) {
      const attractionStrength = (1 - distanceFromMouse / attractionRadius) * 0.15 * star.z;
      x += dx * attractionStrength;
      y += dy * attractionStrength;
    }

    // Wrap around screen
    if (x < -50) x += width + 100;
    if (x > width + 50) x -= width + 100;
    if (y < -50) y += height + 100;
    if (y > height + 50) y -= height + 100;

    // Recalculate distance after position adjustments
    const finalDx = x - mouseRef.current.x;
    const finalDy = y - mouseRef.current.y;
    const finalDistance = Math.sqrt(finalDx * finalDx + finalDy * finalDy);

    // Warp effect when mouse moves fast
    let warpX = 0;
    let warpY = 0;
    if (mouseSpeed > 2 && finalDistance < 300) {
      const warpStrength = (1 - finalDistance / 300) * mouseSpeed * 0.5;
      warpX = (finalDx / finalDistance) * warpStrength * star.z;
      warpY = (finalDy / finalDistance) * warpStrength * star.z;
    }

    x += warpX;
    y += warpY;

    // Twinkle effect - more dynamic
    const twinkle = (Math.sin(time * star.twinkleSpeed + star.twinkleOffset) * 0.5 + 0.5) *
                    (Math.sin(time * star.twinkleSpeed * 1.7 + star.twinkleOffset) * 0.3 + 0.7);
    const currentOpacity = star.opacity * twinkle;

    // Size pulsing based on depth and time
    const sizePulse = 1 + Math.sin(time * 2 + star.twinkleOffset) * 0.2 * (star.z / 4);
    const currentSize = star.size * sizePulse;

    // Glow intensity increases when mouse is near - reduced for softer effect
    const mouseGlowBoost = finalDistance < 200 ? (1 - finalDistance / 200) * 0.25 : 0;

    // Draw outer glow - softer
    const glowSize = currentSize * (3 + mouseGlowBoost * 2);
    const gradient = ctx.createRadialGradient(x, y, 0, x, y, glowSize);
    const glowOpacity = currentOpacity * (0.25 + mouseGlowBoost);
    gradient.addColorStop(0, star.color.replace('1)', `${currentOpacity * 0.8})`));
    gradient.addColorStop(0.3, star.color.replace('1)', `${glowOpacity * 0.4})`));
    gradient.addColorStop(1, 'transparent');

    ctx.beginPath();
    ctx.arc(x, y, glowSize, 0, Math.PI * 2);
    ctx.fillStyle = gradient;
    ctx.fill();

    // Draw star core with cross flare for brighter stars
    if (star.size > 1.5 && currentOpacity > 0.5) {
      const flareLength = currentSize * 3 * twinkle;
      ctx.strokeStyle = star.color.replace('1)', `${currentOpacity * 0.6})`);
      ctx.lineWidth = 1;
      ctx.beginPath();
      ctx.moveTo(x - flareLength, y);
      ctx.lineTo(x + flareLength, y);
      ctx.moveTo(x, y - flareLength);
      ctx.lineTo(x, y + flareLength);
      ctx.stroke();
    }

    // Draw core
    ctx.beginPath();
    ctx.arc(x, y, currentSize, 0, Math.PI * 2);
    ctx.fillStyle = star.color.replace('1)', `${currentOpacity})`);
    ctx.fill();

    return { x, y, z: star.z, opacity: currentOpacity };
  }, []);

  const drawShootingStar = useCallback((ctx: CanvasRenderingContext2D, star: ShootingStar) => {
    const tailX = star.x - Math.cos(star.angle) * star.length;
    const tailY = star.y - Math.sin(star.angle) * star.length;

    const gradient = ctx.createLinearGradient(tailX, tailY, star.x, star.y);
    gradient.addColorStop(0, 'transparent');
    gradient.addColorStop(0.5, `rgba(167, 139, 250, ${star.opacity * 0.3})`);
    gradient.addColorStop(1, `rgba(255, 255, 255, ${star.opacity})`);

    ctx.beginPath();
    ctx.moveTo(tailX, tailY);
    ctx.lineTo(star.x, star.y);
    ctx.strokeStyle = gradient;
    ctx.lineWidth = 2;
    ctx.lineCap = 'round';
    ctx.stroke();

    // Bright head
    const headGlow = ctx.createRadialGradient(star.x, star.y, 0, star.x, star.y, 8);
    headGlow.addColorStop(0, `rgba(255, 255, 255, ${star.opacity})`);
    headGlow.addColorStop(0.5, `rgba(167, 139, 250, ${star.opacity * 0.5})`);
    headGlow.addColorStop(1, 'transparent');
    ctx.beginPath();
    ctx.arc(star.x, star.y, 8, 0, Math.PI * 2);
    ctx.fillStyle = headGlow;
    ctx.fill();
  }, []);

  const drawRipple = useCallback((ctx: CanvasRenderingContext2D, ripple: Ripple) => {
    const gradient = ctx.createRadialGradient(
      ripple.x, ripple.y, ripple.radius * 0.8,
      ripple.x, ripple.y, ripple.radius
    );
    // Softer ripples
    const softOpacity = ripple.opacity * 0.5;
    gradient.addColorStop(0, 'transparent');
    gradient.addColorStop(0.5, ripple.color.replace('1)', `${softOpacity * 0.2})`));
    gradient.addColorStop(1, 'transparent');

    ctx.beginPath();
    ctx.arc(ripple.x, ripple.y, ripple.radius, 0, Math.PI * 2);
    ctx.strokeStyle = ripple.color.replace('1)', `${softOpacity * 0.7})`);
    ctx.lineWidth = 1.5;
    ctx.stroke();

    // Inner glow
    ctx.beginPath();
    ctx.arc(ripple.x, ripple.y, ripple.radius, 0, Math.PI * 2);
    ctx.fillStyle = gradient;
    ctx.fill();
  }, []);

  const drawParticle = useCallback((ctx: CanvasRenderingContext2D, particle: Particle) => {
    const gradient = ctx.createRadialGradient(
      particle.x, particle.y, 0,
      particle.x, particle.y, particle.size * 2
    );
    // Softer particles
    const softOpacity = particle.opacity * 0.6;
    gradient.addColorStop(0, particle.color.replace('1)', `${softOpacity})`));
    gradient.addColorStop(0.5, particle.color.replace('1)', `${softOpacity * 0.4})`));
    gradient.addColorStop(1, 'transparent');

    ctx.beginPath();
    ctx.arc(particle.x, particle.y, particle.size * 2, 0, Math.PI * 2);
    ctx.fillStyle = gradient;
    ctx.fill();

    // Core
    ctx.beginPath();
    ctx.arc(particle.x, particle.y, particle.size * 0.5, 0, Math.PI * 2);
    ctx.fillStyle = particle.color.replace('1)', `${softOpacity * 0.8})`);
    ctx.fill();
  }, []);

  const drawDustParticle = useCallback((
    ctx: CanvasRenderingContext2D,
    dust: DustParticle,
    time: number
  ) => {
    const wobbleX = Math.sin(time * dust.wobbleSpeed + dust.wobbleOffset) * 2;
    const x = dust.x + wobbleX;
    const y = dust.y;

    const gradient = ctx.createRadialGradient(x, y, 0, x, y, dust.size * 2);
    gradient.addColorStop(0, `rgba(199, 210, 254, ${dust.opacity})`);
    gradient.addColorStop(1, 'transparent');

    ctx.beginPath();
    ctx.arc(x, y, dust.size * 2, 0, Math.PI * 2);
    ctx.fillStyle = gradient;
    ctx.fill();
  }, []);

  const drawEnergyPulse = useCallback((ctx: CanvasRenderingContext2D, pulse: EnergyPulse) => {
    const gradient = ctx.createRadialGradient(
      pulse.x, pulse.y, pulse.radius - pulse.width,
      pulse.x, pulse.y, pulse.radius + pulse.width
    );
    // Softer energy pulse
    const softOpacity = pulse.opacity * 0.5;
    gradient.addColorStop(0, 'transparent');
    gradient.addColorStop(0.3, `rgba(167, 139, 250, ${softOpacity * 0.2})`);
    gradient.addColorStop(0.5, `rgba(167, 139, 250, ${softOpacity * 0.6})`);
    gradient.addColorStop(0.7, `rgba(167, 139, 250, ${softOpacity * 0.2})`);
    gradient.addColorStop(1, 'transparent');

    ctx.beginPath();
    ctx.arc(pulse.x, pulse.y, pulse.radius, 0, Math.PI * 2);
    ctx.strokeStyle = `rgba(167, 139, 250, ${softOpacity * 0.7})`;
    ctx.lineWidth = pulse.width;
    ctx.stroke();

    // Inner glow
    ctx.beginPath();
    ctx.arc(pulse.x, pulse.y, pulse.radius, 0, Math.PI * 2);
    ctx.fillStyle = gradient;
    ctx.fill();
  }, []);

  const drawAurora = useCallback((
    ctx: CanvasRenderingContext2D,
    width: number,
    height: number,
    time: number
  ) => {
    const centerX = width / 2;
    const centerY = height * 0.52; // Centered on the name in hero section

    // Create smooth, perfectly elliptical orbits (reduced from 10 to 7)
    const orbitCount = 7;

    for (let o = 0; o < orbitCount; o++) {
      const depth = o / orbitCount;

      // Smooth orbit sizing - tighter orbits for smaller galaxy
      const baseRadius = 80 + depth * 550;

      // Ellipse dimensions for 3D perspective (viewing rings from an angle)
      const radiusX = baseRadius * 2.2;
      const radiusY = baseRadius * 0.35;

      // Gentle pulsing - very smooth
      const pulse = Math.sin(time * 0.2 + o * 0.5) * 0.15 + 0.85;

      // Opacity based on depth - inner rings slightly brighter
      const baseOpacity = (0.035 - depth * 0.02) * pulse;

      // Color gradient from violet (inner) to magenta (outer)
      const hue = 245 + depth * 15;
      const saturation = 80;
      const lightness = 60 + depth * 10;

      // Draw perfect ellipse using arc with scale transform
      ctx.save();
      ctx.translate(centerX, centerY);
      ctx.scale(1, radiusY / radiusX); // Flatten to ellipse

      ctx.beginPath();
      ctx.arc(0, 0, radiusX, 0, Math.PI * 2);
      ctx.restore();

      // Stroke the orbit ring
      ctx.strokeStyle = `hsla(${hue}, ${saturation}%, ${lightness}%, ${baseOpacity * 2})`;
      ctx.lineWidth = (3 - depth * 1.5) * pulse;
      ctx.stroke();

      // Subtle fill glow
      const gradient = ctx.createRadialGradient(
        centerX, centerY, baseRadius * 0.3,
        centerX, centerY, baseRadius * 1.5
      );
      gradient.addColorStop(0, 'transparent');
      gradient.addColorStop(0.4, `hsla(${hue}, ${saturation}%, ${lightness}%, ${baseOpacity * 0.3})`);
      gradient.addColorStop(0.6, `hsla(${hue + 15}, ${saturation}%, ${lightness}%, ${baseOpacity * 0.4})`);
      gradient.addColorStop(1, 'transparent');

      ctx.fillStyle = gradient;
      ctx.fill();
    }

    // Central glow core - smooth pulsing (adjusted for smaller galaxy)
    const coreGlow = ctx.createRadialGradient(centerX, centerY, 0, centerX, centerY, 200);
    const corePulse = Math.sin(time * 0.3) * 0.2 + 0.8;
    coreGlow.addColorStop(0, `rgba(167, 139, 250, ${0.08 * corePulse})`);
    coreGlow.addColorStop(0.25, `rgba(129, 140, 248, ${0.05 * corePulse})`);
    coreGlow.addColorStop(0.5, `rgba(167, 139, 250, ${0.03 * corePulse})`);
    coreGlow.addColorStop(1, 'transparent');

    ctx.beginPath();
    ctx.arc(centerX, centerY, 200, 0, Math.PI * 2);
    ctx.fillStyle = coreGlow;
    ctx.fill();

    // Orbiting light particles - smooth elliptical paths (reduced for smaller galaxy)
    for (let p = 0; p < 4; p++) {
      // Each particle orbits at different speed and radius
      const orbitRadius = 150 + p * 120;
      const orbitSpeed = 0.15 - p * 0.015; // Outer orbits slightly slower
      const angle = time * orbitSpeed + (p * Math.PI * 2 / 4);

      // Perfect elliptical position
      const particleX = centerX + Math.cos(angle) * orbitRadius * 2.2;
      const particleY = centerY + Math.sin(angle) * orbitRadius * 0.35;

      // Particle glow
      const particleGradient = ctx.createRadialGradient(particleX, particleY, 0, particleX, particleY, 50);
      const particleOpacity = 0.25 * (Math.sin(time * 0.5 + p) * 0.3 + 0.7);

      particleGradient.addColorStop(0, `rgba(255, 255, 255, ${particleOpacity})`);
      particleGradient.addColorStop(0.3, `rgba(167, 139, 250, ${particleOpacity * 0.6})`);
      particleGradient.addColorStop(1, 'transparent');

      ctx.beginPath();
      ctx.arc(particleX, particleY, 50, 0, Math.PI * 2);
      ctx.fillStyle = particleGradient;
      ctx.fill();
    }
  }, []);

  const drawConnections = useCallback((
    ctx: CanvasRenderingContext2D,
    positions: { x: number; y: number; z: number; opacity: number }[],
    mouseX: number,
    mouseY: number,
    mouseSpeed: number
  ) => {
    const connectionDistance = 120;
    const mouseConnectionDistance = 180 + mouseSpeed * 10;

    // Connect nearby stars with glowing lines
    for (let i = 0; i < positions.length; i++) {
      if (positions[i].z < 2) continue;

      for (let j = i + 1; j < positions.length; j++) {
        if (positions[j].z < 2) continue;

        const dx = positions[i].x - positions[j].x;
        const dy = positions[i].y - positions[j].y;
        const distance = Math.sqrt(dx * dx + dy * dy);

        if (distance < connectionDistance) {
          // Softer connections
          const opacity = (1 - distance / connectionDistance) * 0.08 *
                         Math.min(positions[i].opacity, positions[j].opacity);

          const gradient = ctx.createLinearGradient(
            positions[i].x, positions[i].y,
            positions[j].x, positions[j].y
          );
          gradient.addColorStop(0, `rgba(167, 139, 250, ${opacity})`);
          gradient.addColorStop(0.5, `rgba(167, 139, 250, ${opacity * 1.1})`);
          gradient.addColorStop(1, `rgba(167, 139, 250, ${opacity})`);

          ctx.beginPath();
          ctx.moveTo(positions[i].x, positions[i].y);
          ctx.lineTo(positions[j].x, positions[j].y);
          ctx.strokeStyle = gradient;
          ctx.lineWidth = 0.4;
          ctx.stroke();
        }
      }

      // Connect to mouse with animated pulse - softer
      const mouseDx = positions[i].x - mouseX;
      const mouseDy = positions[i].y - mouseY;
      const mouseDistance = Math.sqrt(mouseDx * mouseDx + mouseDy * mouseDy);

      if (mouseDistance < mouseConnectionDistance) {
        const baseOpacity = (1 - mouseDistance / mouseConnectionDistance) * 0.2;
        const pulseOpacity = baseOpacity * (0.7 + Math.sin(timeRef.current * 5 + i) * 0.3);

        const gradient = ctx.createLinearGradient(positions[i].x, positions[i].y, mouseX, mouseY);
        gradient.addColorStop(0, `rgba(167, 139, 250, ${pulseOpacity})`);
        gradient.addColorStop(1, `rgba(167, 139, 250, ${pulseOpacity * 0.4})`);

        ctx.beginPath();
        ctx.moveTo(positions[i].x, positions[i].y);
        ctx.lineTo(mouseX, mouseY);
        ctx.strokeStyle = gradient;
        ctx.lineWidth = 0.8 + (1 - mouseDistance / mouseConnectionDistance) * 0.5;
        ctx.stroke();
      }
    }
  }, []);

  const drawNebula = useCallback((
    ctx: CanvasRenderingContext2D,
    width: number,
    height: number,
    mouseX: number,
    mouseY: number,
    time: number
  ) => {
    const nebulaLayers = [
      { x: 0.15, y: 0.25, size: 500, color: [139, 92, 246], speed: 0.15, amplitude: 50 },
      { x: 0.85, y: 0.75, size: 450, color: [99, 102, 241], speed: 0.12, amplitude: 40 },
      { x: 0.5, y: 0.45, size: 600, color: [139, 92, 246], speed: 0.1, amplitude: 60 },
      { x: 0.2, y: 0.8, size: 350, color: [129, 140, 248], speed: 0.18, amplitude: 35 },
      { x: 0.75, y: 0.2, size: 400, color: [167, 139, 250], speed: 0.14, amplitude: 45 },
    ];

    nebulaLayers.forEach((nebula, index) => {
      // Autonomous flowing movement
      const flowX = Math.sin(time * nebula.speed + index * 1.5) * nebula.amplitude;
      const flowY = Math.cos(time * nebula.speed * 0.7 + index) * nebula.amplitude * 0.7;

      // Mouse influence
      const mouseInfluenceX = (mouseX - width / 2) * 0.03;
      const mouseInfluenceY = (mouseY - height / 2) * 0.03;

      const x = width * nebula.x + flowX + mouseInfluenceX;
      const y = height * nebula.y + flowY + mouseInfluenceY;

      // Pulsing opacity - reduced for softer effect
      const pulseOpacity = 0.035 + Math.sin(time * 0.3 + index * 0.5) * 0.012;

      const gradient = ctx.createRadialGradient(x, y, 0, x, y, nebula.size);
      gradient.addColorStop(0, `rgba(${nebula.color.join(',')}, ${pulseOpacity})`);
      gradient.addColorStop(0.4, `rgba(${nebula.color.join(',')}, ${pulseOpacity * 0.35})`);
      gradient.addColorStop(1, 'transparent');

      ctx.fillStyle = gradient;
      ctx.fillRect(0, 0, width, height);
    });
  }, []);

  const drawMouseAura = useCallback((
    ctx: CanvasRenderingContext2D,
    mouseX: number,
    mouseY: number,
    time: number,
    idleTime: number
  ) => {
    // Breathing aura effect when idle - starts at 10% and grows to 30% over time
    // idleTime increases when cursor is still, resets when moving
    const idleProgress = Math.min(idleTime / 3, 1); // Takes 3 seconds to reach full
    const breathingCycle = Math.sin(time * 0.8) * 0.5 + 0.5; // Slow breathing

    // Base opacity ranges from 0.03 (10% of max) to 0.09 (30% of max) based on idle time
    const baseOpacity = 0.03 + (idleProgress * 0.06);
    // Add subtle breathing variation
    const auraOpacity = baseOpacity * (0.85 + breathingCycle * 0.15);

    // Aura size also breathes
    const pulseSize = 120 + Math.sin(time * 0.6) * 30 + (idleProgress * 40);
    const aura = ctx.createRadialGradient(mouseX, mouseY, 0, mouseX, mouseY, pulseSize);

    aura.addColorStop(0, `rgba(167, 139, 250, ${auraOpacity})`);
    aura.addColorStop(0.4, `rgba(167, 139, 250, ${auraOpacity * 0.4})`);
    aura.addColorStop(0.7, `rgba(167, 139, 250, ${auraOpacity * 0.15})`);
    aura.addColorStop(1, 'transparent');

    ctx.fillStyle = aura;
    ctx.fillRect(0, 0, ctx.canvas.width, ctx.canvas.height);

    // Subtle rotating ring that appears when idle (very subtle)
    if (idleProgress > 0.3) {
      const ringOpacity = (idleProgress - 0.3) * 0.1 * breathingCycle;
      const ringRadius = 50 + idleProgress * 30;
      ctx.strokeStyle = `rgba(167, 139, 250, ${ringOpacity})`;
      ctx.lineWidth = 0.5;
      ctx.beginPath();
      ctx.arc(mouseX, mouseY, ringRadius, time * 0.5, time * 0.5 + Math.PI * 1.5);
      ctx.stroke();
    }
  }, []);

  const animate = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const width = canvas.width;
    const height = canvas.height;

    // Calculate mouse speed
    const mouseDx = mouseRef.current.targetX - lastMousePos.current.x;
    const mouseDy = mouseRef.current.targetY - lastMousePos.current.y;
    mouseRef.current.speed = Math.sqrt(mouseDx * mouseDx + mouseDy * mouseDy);
    lastMousePos.current = { x: mouseRef.current.targetX, y: mouseRef.current.targetY };

    // Track idle time (increases when cursor is still, resets when moving)
    if (mouseRef.current.speed < 0.5) {
      idleTimeRef.current += 0.016; // Increase idle time
    } else {
      idleTimeRef.current = Math.max(0, idleTimeRef.current - 0.08); // Gradually reset when moving
    }

    // Smooth mouse following
    mouseRef.current.x += (mouseRef.current.targetX - mouseRef.current.x) * 0.08;
    mouseRef.current.y += (mouseRef.current.targetY - mouseRef.current.y) * 0.08;

    const mouseOffsetX = (mouseRef.current.x - width / 2) / width * 60;
    const mouseOffsetY = (mouseRef.current.y - height / 2) / height * 60;

    // Clear with dark background
    ctx.fillStyle = '#080510';
    ctx.fillRect(0, 0, width, height);

    // Draw aurora (behind everything)
    auroraPhaseRef.current += 0.016;
    drawAurora(ctx, width, height, auroraPhaseRef.current);

    // Draw nebula
    drawNebula(ctx, width, height, mouseRef.current.x, mouseRef.current.y, timeRef.current);

    // Update and draw dust particles
    dustRef.current.forEach(dust => {
      dust.x += dust.speedX;
      dust.y += dust.speedY;

      // Wrap around screen
      if (dust.x < 0) dust.x = width;
      if (dust.x > width) dust.x = 0;
      if (dust.y < 0) dust.y = height;
      if (dust.y > height) dust.y = 0;

      drawDustParticle(ctx, dust, timeRef.current);
    });

    // Randomly spawn shooting stars
    if (Math.random() < 0.003) {
      shootingStarsRef.current.push(createShootingStar(width, height));
    }

    // Periodic energy pulses from cursor
    if (timeRef.current - lastPulseTimeRef.current > 3 && mouseRef.current.speed > 0.5) {
      energyPulsesRef.current.push(createEnergyPulse(mouseRef.current.x, mouseRef.current.y));
      lastPulseTimeRef.current = timeRef.current;
    }

    // Update and draw energy pulses
    energyPulsesRef.current = energyPulsesRef.current.filter(pulse => {
      pulse.radius += 8;
      pulse.opacity = 0.4 * (1 - pulse.radius / pulse.maxRadius);
      pulse.width = 3 * (1 - pulse.radius / pulse.maxRadius) + 1;

      if (pulse.radius < pulse.maxRadius) {
        drawEnergyPulse(ctx, pulse);
        return true;
      }
      return false;
    });

    // Update and draw shooting stars
    shootingStarsRef.current = shootingStarsRef.current.filter(star => {
      star.x += Math.cos(star.angle) * star.speed;
      star.y += Math.sin(star.angle) * star.speed;
      star.life -= 0.015;
      star.opacity = star.life;

      if (star.life > 0 && star.x < width + 100 && star.y < height + 100) {
        drawShootingStar(ctx, star);
        return true;
      }
      return false;
    });

    // Update and draw ripples
    ripplesRef.current = ripplesRef.current.filter(ripple => {
      ripple.radius += ripple.speed;
      ripple.opacity = 0.6 * (1 - ripple.radius / ripple.maxRadius);

      if (ripple.radius < ripple.maxRadius) {
        drawRipple(ctx, ripple);
        return true;
      }
      return false;
    });

    // Update and draw particles
    particlesRef.current = particlesRef.current.filter(particle => {
      particle.x += particle.vx;
      particle.y += particle.vy;
      particle.vy += particle.gravity;
      particle.vx *= 0.98;
      particle.vy *= 0.98;
      particle.life -= 0.02;
      particle.opacity = particle.life;

      if (particle.life > 0) {
        drawParticle(ctx, particle);
        return true;
      }
      return false;
    });

    // Draw stars and collect positions
    const positions: { x: number; y: number; z: number; opacity: number }[] = [];
    starsRef.current.forEach(star => {
      const pos = drawStar(
        ctx, star, timeRef.current, mouseOffsetX, mouseOffsetY,
        mouseRef.current.speed, width, height
      );
      positions.push(pos);
    });

    // Draw connections
    drawConnections(ctx, positions, mouseRef.current.x, mouseRef.current.y, mouseRef.current.speed);

    // Draw mouse aura with idle breathing effect
    drawMouseAura(ctx, mouseRef.current.x, mouseRef.current.y, timeRef.current, idleTimeRef.current);

    timeRef.current += 0.016;
    animationRef.current = requestAnimationFrame(animate);
  }, [
    drawStar, drawConnections, drawNebula, drawMouseAura, drawShootingStar,
    createShootingStar, drawAurora, drawDustParticle, drawRipple, drawParticle,
    drawEnergyPulse, createEnergyPulse
  ]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const handleResize = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
      starsRef.current = createStars(canvas.width, canvas.height);
      dustRef.current = createDustParticles(canvas.width, canvas.height);
    };

    const handleMouseMove = (e: MouseEvent) => {
      mouseRef.current.targetX = e.clientX;
      mouseRef.current.targetY = e.clientY;
      mouseRef.current.isMoving = true;
    };

    const handleTouchMove = (e: TouchEvent) => {
      if (e.touches.length > 0) {
        mouseRef.current.targetX = e.touches[0].clientX;
        mouseRef.current.targetY = e.touches[0].clientY;
      }
    };

    const handleClick = (e: MouseEvent) => {
      // Create ripples on click
      ripplesRef.current.push(createRipple(e.clientX, e.clientY));
      ripplesRef.current.push(createRipple(e.clientX, e.clientY));

      // Create particle burst
      particlesRef.current.push(...createParticleBurst(e.clientX, e.clientY, 25));

      // Create energy pulse
      energyPulsesRef.current.push(createEnergyPulse(e.clientX, e.clientY));
    };

    const handleTouchStart = (e: TouchEvent) => {
      if (e.touches.length > 0) {
        const touch = e.touches[0];
        ripplesRef.current.push(createRipple(touch.clientX, touch.clientY));
        particlesRef.current.push(...createParticleBurst(touch.clientX, touch.clientY, 20));
        energyPulsesRef.current.push(createEnergyPulse(touch.clientX, touch.clientY));
      }
    };

    handleResize();
    mouseRef.current.x = window.innerWidth / 2;
    mouseRef.current.y = window.innerHeight / 2;
    mouseRef.current.targetX = window.innerWidth / 2;
    mouseRef.current.targetY = window.innerHeight / 2;

    window.addEventListener('resize', handleResize);
    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('touchmove', handleTouchMove);
    window.addEventListener('click', handleClick);
    window.addEventListener('touchstart', handleTouchStart);

    animationRef.current = requestAnimationFrame(animate);

    return () => {
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('touchmove', handleTouchMove);
      window.removeEventListener('click', handleClick);
      window.removeEventListener('touchstart', handleTouchStart);
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, [createStars, createDustParticles, animate, createRipple, createParticleBurst, createEnergyPulse]);

  return (
    <canvas
      ref={canvasRef}
      className="fixed inset-0"
      style={{ zIndex: 0, pointerEvents: 'auto' }}
    />
  );
};

export default InteractiveStarfield;
